import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { theme, VStack } from '@sg/garnish';

import { OrderStatus, useFavoriteLineItem } from '@order/features-order-status';
import { getParticipantLineItemGroups } from '@order/features-ordering';
import { buildShareLineItemUrl } from '@order/utils';

import { type PartialLineItem } from '../../../../graphql';
import { type ReorderOrder } from '../../../../hooks';
import { useShareLineItem } from '../../../../hooks';
import { OrderStatusLineItemCard } from '../OrderStatusLineItemCard';

export const OrderStatusLineItems = (props: OrderStatusLineItemsProps) => {
  const { order, lineItems, isGroupOrder } = props;

  const { formatMessage } = useIntl();

  const { isFavoringLineItem, handleFavoriteLineItem } = useFavoriteLineItem();
  const { handleShareLineItem } = useShareLineItem();

  // ─── Derived ──────────────────────────────────────────────────────────────

  const lineItemGroups: readonly LineItemGroup[] = isGroupOrder
    ? getParticipantLineItemGroups(lineItems)
    : [{ id: SOLO_ORDER_GROUP_ID, isMe: true, lineItems }];

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <VStack gap={theme.spacing['4']}>
      {lineItemGroups.map((lineItemGroup) => {
        return (
          <VStack key={lineItemGroup.id} gap={0}>
            {isGroupOrder ? (
              <OrderStatus.LineItemGroupHeader
                participantName={lineItemGroup.displayName}
                isCurrentUser={lineItemGroup.isMe}
              />
            ) : null}

            <VStack gap={0} hasDivider dividerColor={theme.colors.LIGHT}>
              {lineItemGroup.lineItems.map((lineItem) => {
                const url = buildShareLineItemUrl(lineItem.slug);

                return (
                  <OrderStatusLineItemCard
                    key={lineItem.id ?? lineItem.product.name}
                    lineItem={lineItem}
                    order={order}
                    onFavorite={handleFavoriteLineItem}
                    isFetchingFavorite={isFavoringLineItem}
                    isFavorite={lineItem.favorited}
                    onShare={handleShareLineItem}
                    shareBtnLabel={formatMessage(messages.shareProduct)}
                    shareTooltipLabel={formatMessage(messages.shareProductTooltip)}
                    shareUrl={url}
                  />
                );
              })}
            </VStack>
          </VStack>
        );
      })}
    </VStack>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  shareProduct: {
    defaultMessage: 'Share',
    description: 'Order Status | Line Items | Share Product',
  },
  shareProductTooltip: {
    defaultMessage: 'Copied link',
    description: 'Order Status | Line Items | Share Product Tooltip',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SOLO_ORDER_GROUP_ID = 'solo-order';

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusLineItemsProps = {
  order: ReorderOrder;
  lineItems: readonly PartialLineItem[];
  isGroupOrder: boolean;
};

type LineItemGroup = {
  id: string;
  displayName?: string | null;
  isMe: boolean;
  lineItems: readonly PartialLineItem[];
};
